import { ReactNode } from 'react'

import clsx from 'clsx'

interface Props {
  children?: ReactNode
  background?: 'primary' | 'secondary' | 'caution' | 'info'
  multipleItems?: ReactNode[]
  checkbox?: boolean
  thumbnail?: boolean
  bottomContents?: ReactNode
  sticky?: 0 | 40 | 58
  width?: 40 | 64 | 88 | 128 | 160 | 188 | 200 | 240 | 320 | 420
  align?: 'top' | 'middle'
  type?: 'default' | 'high' | 'middle' | 'low' | 'x-low'
  innerGap?: 4 | 8 | 10
  innerCenter?: boolean
  scrollShadow?: boolean
  radius?: 'first' | 'last' | 'middle'
  id?: string
}

const TableRowItem = ({
  children,
  background = 'primary',
  checkbox = false,
  thumbnail = false,
  multipleItems,
  bottomContents,
  sticky,
  width,
  align = 'middle',
  type = 'default',
  innerGap,
  innerCenter = false,
  scrollShadow = false,
  radius,
  id
}: Props) => {
  const stickyPosition =
    sticky == 0 ? 'left-0' : sticky == 40 ? 'left-10' : sticky == 58 ? 'left-[58px]' : ''
  const widthClass = !checkbox
    ? width == 40
      ? 'min-w-10 w-10 max-w-10'
      : width == 64
        ? 'min-w-16 w-16 max-w-16'
        : width == 88
          ? 'min-w-22 w-22 max-w-22'
          : width == 128
            ? 'min-w-32 w-32 max-w-32'
            : width == 160
              ? 'min-w-40 w-40 max-w-40'
              : width == 188
                ? 'min-w-47 w-47 max-w-47'
                : width == 200
                  ? 'min-w-50 w-50 max-w-50'
                  : width == 240
                    ? 'min-w-60 w-60 max-w-60'
                    : width == 320
                      ? 'min-w-80 w-80 max-w-80'
                      : width == 420
                        ? 'min-w-105 w-105 max-w-105'
                        : 'w-fit'
    : ''
  const gapValue = innerGap && innerGap / 4

  return (
    <td
      id={id}
      className={clsx(
        'relative',
        'first:pl-3 last:pr-3',
        stickyPosition &&
          clsx(
            `sticky ${stickyPosition} z-sticky`,
            'before:content-[""]',
            'before:absolute before:inset-x-0 before:top-0 before:h-[calc(100%-1px)]',
            background == 'caution'
              ? 'before:bg-surface-caution'
              : background == 'info'
                ? 'before:bg-surface-info'
                : 'before:bg-surface',
            'before:-z-1',
            'group-hover:before:bg-surface-hover'
          ),
        checkbox && 'w-10.5 max-w-10.5 pr-3.5',
        thumbnail && 'flex w-16 max-w-16 items-center justify-center',
        widthClass,
        align == 'middle' ? 'align-middle' : 'align-top',
        type == 'x-low'
          ? 'min-h-9 px-1.5 py-1'
          : type == 'low'
            ? 'min-h-9 px-3 py-1'
            : type == 'middle'
              ? 'min-h-8 px-3 py-2'
              : type == 'high'
                ? 'min-h-10 px-2 py-3'
                : 'px-1.5 py-2',
        scrollShadow && 'drop-shadow-[1px_0_0_rgba(235,235,235,1)]',
        radius == 'first' && 'border-surface rounded-l-2xl border-y-4',
        radius == 'last' && 'border-surface rounded-r-2xl border-y-4',
        radius == 'middle' && 'border-surface border-y-4',
        background == 'secondary' && 'bg-secondary',
        background == 'caution' && 'bg-surface-caution',
        background == 'info' && 'bg-surface-info'
      )}
    >
      {multipleItems ? (
        <>
          <div className='flex gap-x-3'>
            {multipleItems.map((multipleItem: ReactNode, index: number) => (
              <div key={index}>{multipleItem}</div>
            ))}
          </div>
          <div className='mt-2'>{bottomContents}</div>
        </>
      ) : (
        <div
          className={clsx(
            'flex',
            innerGap && `gap-${gapValue}`,
            innerCenter && 'items-center',
            !checkbox && `flex-col`
          )}
        >
          {children}
        </div>
      )}
    </td>
  )
}

export default TableRowItem
